import { cn } from '@/lib/utils';
import type { MouseEvent as ReactMouseEvent } from 'react';

interface ChartResizeHandlesProps {
    isResizing: boolean;
    livePct: number | null;
    onResizeStart: (side: 'left' | 'right', event: ReactMouseEvent) => void;
}

/**
 * Hover-only vertical pills on a media block's left/right edges (same chrome
 * language as the table editor's edge pills): drag to resize proportionally,
 * with a live width badge while dragging.
 *
 * Charts and images share this, so the hover group is `media` rather than
 * either block's own name — Tailwind compiles group names statically, so it
 * cannot be passed in as a prop.
 */
export function ChartResizeHandles({
    isResizing,
    livePct,
    onResizeStart,
}: ChartResizeHandlesProps) {
    return (
        <>
            {(['left', 'right'] as const).map((side) => (
                <button
                    key={side}
                    type="button"
                    data-testid={`chart-resize-handle-${side}`}
                    title="Arraste para redimensionar"
                    aria-label="Arraste para redimensionar"
                    onMouseDown={(event) => onResizeStart(side, event)}
                    onClick={(event) => event.stopPropagation()}
                    className={cn(
                        'absolute top-1/2 z-30 h-12 w-1.5 -translate-y-1/2 cursor-ew-resize rounded-full bg-slate-500/60 ring-1 ring-white transition-opacity hover:bg-slate-600/80',
                        side === 'left' ? 'left-1' : 'right-1',
                        isResizing
                            ? 'opacity-100'
                            : 'opacity-0 group-data-[hover-intent=on]/media:opacity-100',
                    )}
                />
            ))}
            {isResizing && livePct !== null && (
                <span className="absolute -top-7 left-1/2 z-30 -translate-x-1/2 rounded-full bg-slate-900/80 px-2 py-0.5 text-xs font-medium text-white shadow-sm">
                    {Math.round(livePct)}%
                </span>
            )}
        </>
    );
}
